import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Input,
  Row,
  Col,
} from "reactstrap";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import Select from "react-select";
import ReactQuill from "react-quill";
import {
  SINGLE_CORRECT_POSITIVE_OPTIONS,
  MULTI_CORRECT_POSITIVE_OPTIONS,
  ANSWER_TYPES,
} from "components/constant";
import { SELECT_CUSTOM_STYLES, REGEX } from "constants/CommonConstants";
import { addQuestion } from "action/question";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

const initialQBQuestion = {
  question: {
    english: "",
    hindi: "",
    error: "",
  },
  type: { value: null, error: "" },
  level: { value: null, error: "" },
  marks: { value: "", error: "" },
  options: {
    value: [
      { textEnglish: "", textHindi: "", grade: 0 },
      { textEnglish: "", textHindi: "", grade: 0 },
    ],
    error: "",
  },
};

const AddQuestion = ({
  isOpen,
  onClose,
  onSave,
  mode,
  selectedIndex,
  initialData,
  // negativeGrading,
  assessmentLanguage,
  questionBankID,
  levelOptions,
}) => {
  const { notify } = useContext(NotificationContext);
  const [currentQB, setCurrentQB] = useState(initialQBQuestion);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentQB(initialQBQuestion);
    } else if (initialData) {
      const incomingOptions = Array.isArray(initialData.options)
        ? initialData.options
        : initialData.options?.value || [];
      const fixedOptions = incomingOptions.map((option) => {
        if (hasEnglish && hasHindi) {
          return {
            textEnglish: option.textEnglish || "",
            textHindi: option.textHindi || "",
            grade: option.grade ?? 0,
          };
        }
        return {
          textEnglish: option.textEnglish || option.text || "",
          textHindi: option.textHindi || "",
          grade: option.grade ?? 0,
        };
      });
      setCurrentQB({
        ...initialData,
        options: { value: fixedOptions, error: "" },
      });
    } else {
      setCurrentQB(initialQBQuestion);
    }
  }, [isOpen, initialData]);

  const qbPanelMode = mode;

  const normalizedLanguages = assessmentLanguage
    ? assessmentLanguage
        .split(",")
        .map((language) => language.trim().toUpperCase())
    : ["ENGLISH"];

  const hasEnglish = normalizedLanguages.includes("ENGLISH");
  const hasHindi = normalizedLanguages.includes("HINDI");

  const hasContent = (html = "") => {
    if (!html) return false;

    const el = document.createElement("div");
    el.innerHTML = html;

    const hasText = el.textContent.trim().length > 0;
    const hasImage = el.querySelector("img");

    return hasText || hasImage;
  };

  const getQBValidationError = (name, value) => {
    switch (name) {
      case "question": {
        const hasEn = hasContent(value?.english);
        const hasHi = hasContent(value?.hindi);

        if (!hasEn && !hasHi) {
          return "Question is required";
        }

        return "";
      }

      case "type":
        return !value ? "Please select answer type" : "";

      case "marks":
        if (value === "" || value === null) return "Please enter marks";
        if (!REGEX.OTP_STRING_REGEX.test(String(value)))
          return "Enter a valid number";
        if (Number(value) <= 0) return "Marks must be greater than 0";
        return "";

      case "level":
        return !value ? "Please select level" : "";

      case "options": {
        if (currentQB.type.value === "TRUE_FALSE") {
          const hasCorrectAnswer = value.some((option) => option.grade === 1);

          return hasCorrectAnswer ? "" : "Please select a correct answer";
        }

        const filledOptions = value.filter((o) => {
          if (hasEnglish && !hasHindi) {
            return hasContent(o.textEnglish);
          }

          if (!hasEnglish && hasHindi) {
            return hasContent(o.textHindi);
          }

          if (hasEnglish && hasHindi) {
            return hasContent(o.textEnglish) || hasContent(o.textHindi);
          }

          return false;
        });

        if (filledOptions.length < 2) return "Please enter at least 2 options";

        return "";
      }

      default:
        return "";
    }
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const updateOptionQuill = (index, value, key) => {
    setCurrentQB((prev) => {
      const updated = [...prev.options.value];

      updated[index] = {
        ...updated[index],
        [key]: value,
      };

      return {
        ...prev,
        options: {
          ...prev.options,
          value: updated,
          error: "",
        },
      };
    });
  };

  const handleAnswerTypeChange = (selected) => {
    setCurrentQB((prev) => {
      let optionsValue = [];

      if (selected.value === "TRUE_FALSE") {
        optionsValue = [
          { text: "True", grade: 0 },
          { text: "False", grade: 0 },
        ];
      } else if (selected.value === "DESC") {
        optionsValue = [];
      } else {
        optionsValue = [
          { textEnglish: "", textHindi: "", grade: 0 },
          { textEnglish: "", textHindi: "", grade: 0 },
        ];
      }

      return {
        ...prev,
        type: { value: selected.value, error: "" },
        options: {
          value: optionsValue,
          error: "",
        },
      };
    });
  };

  const validateQBQuestion = () => {
    const qError = getQBValidationError("question", currentQB.question);
    const tError = getQBValidationError("type", currentQB.type.value);
    const mError = getQBValidationError("marks", currentQB.marks.value);
    const lError = getQBValidationError("level", currentQB.level.value);
    const oError = currentQB.type.value
      ? getQBValidationError("options", currentQB.options.value)
      : "";

    setCurrentQB((prev) => ({
      ...prev,
      question: { ...prev.question, error: qError },
      type: { ...prev.type, error: tError },
      marks: { ...prev.marks, error: mError },
      level: { ...prev.level, error: lError },
      options: { ...prev.options, error: oError },
    }));

    return !qError && !tError && !mError && !lError && !oError;
  };

  const getRemainingPositivePercentage = (currentIndex) => {
    return Math.max(
      0,
      1 -
        currentQB.options.value.reduce((sum, opt, idx) => {
          if (
            idx !== currentIndex &&
            typeof opt.grade === "number" &&
            opt.grade > 0
          ) {
            return sum + opt.grade;
          }
          return sum;
        }, 0),
    );
  };

  const handleQBOptionGradeChange = (index, grade) => {
    const updated = [...currentQB.options.value];

    updated[index] = {
      ...updated[index],
      grade,
    };

    setCurrentQB((p) => ({
      ...p,
      options: {
        ...p.options,
        value: updated,
        error: "",
      },
    }));
  };

  const addQBOption = () => {
    if (currentQB.options.value.length >= 5) return;

    setCurrentQB((p) => ({
      ...p,
      options: {
        value: [
          ...p.options.value,
          hasEnglish && hasHindi
            ? { textEnglish: "", textHindi: "", grade: 0 }
            : hasEnglish
              ? { textEnglish: "", grade: 0 }
              : { textHindi: "", grade: 0 },
        ],
        error: "",
      },
    }));
  };

  const removeQBOption = (index) => {
    if (currentQB.options.value.length <= 2) return;

    setCurrentQB((p) => ({
      ...p,
      options: {
        value: p.options.value.filter((_, i) => i !== index),
        error: "",
      },
    }));
  };

  const getFilteredGradeOptions = (index) => {
    const baseOptions = getGradeOptions();

    // SINGLE_CHOICE / TRUE_FALSE
    if (
      currentQB.type.value === "SINGLE_CHOICE" ||
      currentQB.type.value === "TRUE_FALSE"
    ) {
      const hasHundredSelected = currentQB.options.value.some(
        (opt, idx) => idx !== index && opt.grade === 1,
      );

      if (!hasHundredSelected) {
        return baseOptions;
      }

      return baseOptions.filter(
        (opt) => opt.value === 0 || currentQB.options.value[index]?.grade === 1,
      );
    }

    // MULTI
    if (currentQB.type.value === "MULTIPLE_CHOICE") {
      const remaining = Number(
        getRemainingPositivePercentage(index).toFixed(6),
      );

      return baseOptions.filter((opt) => {
        if (opt.value < 0) return true;
        return opt.value <= remaining;
      });
    }

    return baseOptions;
  };

  const getImageCount = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;

    return div.querySelectorAll("img").length;
  };

  const handleQuillChange = (value, callback) => {
    if (getImageCount(value) > 5) {
      notify("danger", `Maximum 5 images are allowed`);
      return;
    }

    callback(value);
  };

  const processAnswers = (options, answerType, languageKey) => {
    let answers = options.map((opt) => ({
      answer: answerType === "TRUE_FALSE" ? opt.text : opt[languageKey],
      grade: Number((opt.grade * 100).toFixed(4)),
    }));

    if (answerType === "MULTIPLE_CHOICE") {
      const positiveAnswers = answers.filter((a) => a.grade > 0);
      const sum = positiveAnswers.reduce((acc, curr) => acc + curr.grade, 0);

      // Distribute any minor float precision difference to the last positive answer
      if (sum > 99 && sum !== 100) {
        const diff = 100 - sum;
        for (let i = answers.length - 1; i >= 0; i--) {
          if (answers[i].grade > 0) {
            answers[i].grade = Number((answers[i].grade + diff).toFixed(4));
            break;
          }
        }
      }
    }
    return answers;
  };

  const addQBQuestion = async () => {
    if (!validateQBQuestion()) return;

    setIsSubmitting(true);

    const payload = {
      questionBankID,
      questionDifficultyLevelID: currentQB.level.value,
      questions: [],
    };

    if (hasEnglish) {
      payload.questions.push({
        language: "ENGLISH",
        question: currentQB.question.english,
        marks: Number(currentQB.marks.value),
        answerType: currentQB.type.value,
        status: "ACTIVE",
        answers: processAnswers(currentQB.options.value, currentQB.type.value, "textEnglish"),
      });
    }

    if (hasHindi) {
      payload.questions.push({
        language: "HINDI",
        question: currentQB.question.hindi,
        marks: Number(currentQB.marks.value),
        answerType: currentQB.type.value,
        status: "ACTIVE",
        answers: processAnswers(currentQB.options.value, currentQB.type.value, "textHindi"),
      });
    }

    try {
      const response = await addQuestion(payload);

      if (response?.code === 200) {
        notify("success", response.message);

        onSave(); // refresh parent listing

        setCurrentQB(initialQBQuestion);
        onClose();
      } else {
        const message = Array.isArray(response?.message)
          ? response.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : response?.message || "Failed to add question";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to add question");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarksChange = (value) => {
    if (value !== "" && !REGEX.ONLY_DIGITS_REGEX.test(value)) {
      return;
    }

    setCurrentQB((prev) => ({
      ...prev,
      marks: {
        ...prev.marks,
        value,
        error: "",
      },
    }));
  };

  const saveQBQuestion = () => {
    if (!validateQBQuestion()) return;

    onSave(
      {
        question: currentQB.question,
        type: currentQB.type.value,
        marks: currentQB.marks.value,
        level: currentQB.level.value,
        options: [...currentQB.options.value],
        // negativeGrading,
      },
      selectedIndex,
      qbPanelMode,
    );

    setCurrentQB(initialQBQuestion);
    onClose();
  };

  const getGradeOptions = () => {
    const type = currentQB.type.value;

    // Description has no grading
    if (type === "DESC") return [];

    // Treat TRUE_FALSE same as SINGLE_CHOICE
    if (type === "SINGLE_CHOICE" || type === "TRUE_FALSE") {
      // return negativeGrading
      //   ? SINGLE_CORRECT_NEGATIVE_OPTIONS
      //   : SINGLE_CORRECT_POSITIVE_OPTIONS;
      return SINGLE_CORRECT_POSITIVE_OPTIONS;
    }

    if (type === "MULTIPLE_CHOICE") {
      // return negativeGrading
      //   ? MULTI_CORRECT_NEGATIVE_OPTIONS
      //   : MULTI_CORRECT_POSITIVE_OPTIONS;
      return MULTI_CORRECT_POSITIVE_OPTIONS;
    }

    return [];
  };

  const gradeOptions = getGradeOptions();

  return (
    <>
      {isOpen && (
        <RightPanelCard
          isOpen={isOpen}
          onClose={onClose}
          title={qbPanelMode === "VIEW" ? "View Question" : "Add Question"}
          fullHeight
          enableScroll={true}
        >
          <Card className="shadow-none border-0">
            <CardBody>
              {/* Question */}
              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={onClose}
                ></i>
              </div>
              <FormGroup>
                <Label>
                  Question <span className="text-danger">*</span>
                </Label>

                <div
                  className={`question-quill quill-wrapper ${
                    currentQB.question.error ? "quill-invalid" : ""
                  }`}
                >
                  {assessmentLanguage === "English" && (
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      placeholder="Enter question"
                      value={currentQB.question.english}
                      onChange={(value) =>
                        handleQuillChange(value, (validatedValue) =>
                          setCurrentQB((p) => ({
                            ...p,
                            question: {
                              ...p.question,
                              english: validatedValue,
                              error: "",
                            },
                          })),
                        )
                      }
                      onBlur={() =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            error: getQBValidationError("question", p.question),
                          },
                        }))
                      }
                    />
                  )}

                  {assessmentLanguage === "Hindi" && (
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      placeholder="प्रश्न हिंदी में लिखें"
                      value={currentQB.question.hindi}
                      onChange={(value) =>
                        handleQuillChange(value, (validatedValue) =>
                          setCurrentQB((p) => ({
                            ...p,
                            question: {
                              ...p.question,
                              hindi: validatedValue,
                              error: "",
                            },
                          })),
                        )
                      }
                      onBlur={() =>
                        setCurrentQB((p) => ({
                          ...p,
                          question: {
                            ...p.question,
                            error: getQBValidationError("question", p.question),
                          },
                        }))
                      }
                    />
                  )}

                  {assessmentLanguage === "English, Hindi" && (
                    <>
                      <Label className="text-muted small">
                        Question (English)
                      </Label>
                      <ReactQuill
                        theme="snow"
                        modules={quillModules}
                        placeholder="Enter question in English"
                        value={currentQB.question.english || ""}
                        onChange={(value) =>
                          handleQuillChange(value, (validatedValue) =>
                            setCurrentQB((p) => ({
                              ...p,
                              question: {
                                ...p.question,
                                english: validatedValue,
                                error: "",
                              },
                            })),
                          )
                        }
                      />

                      <Label className="text-muted small mt-3">
                        Question (Hindi)
                      </Label>
                      <ReactQuill
                        theme="snow"
                        modules={quillModules}
                        placeholder="प्रश्न हिंदी में लिखें"
                        value={currentQB.question.hindi || ""}
                        onChange={(value) =>
                          handleQuillChange(value, (validatedValue) =>
                            setCurrentQB((p) => ({
                              ...p,
                              question: {
                                ...p.question,
                                hindi: validatedValue,
                                error: "",
                              },
                            })),
                          )
                        }
                      />
                    </>
                  )}
                </div>

                {currentQB.question.error && (
                  <small className="invalid-feedback d-block">
                    {currentQB.question.error}
                  </small>
                )}
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>
                        Answer Type <span className="text-danger">*</span>
                      </Label>
                      <Select
                        placeholder="Select answer type"
                        options={ANSWER_TYPES}
                        styles={SELECT_CUSTOM_STYLES}
                        value={
                          currentQB.type.value
                            ? ANSWER_TYPES.find(
                                (opt) => opt.value === currentQB.type.value,
                              )
                            : null
                        }
                        onChange={handleAnswerTypeChange}
                        className={currentQB.type.error ? "is-invalid" : ""}
                      />
                      {currentQB.type.error && (
                        <small className="invalid-feedback d-block">
                          {currentQB.type.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label>
                        Level <span className="text-danger">*</span>
                      </Label>
                      <Select
                        placeholder="Select level"
                        options={levelOptions}
                        styles={SELECT_CUSTOM_STYLES}
                        value={
                          currentQB.level.value
                            ? levelOptions.find(
                                (opt) => opt.value === currentQB.level.value,
                              )
                            : null
                        }
                        onChange={(selected) =>
                          setCurrentQB((prev) => ({
                            ...prev,
                            level: {
                              value: selected.value,
                              error: "",
                            },
                          }))
                        }
                        className={currentQB.level.error ? "is-invalid" : ""}
                      />
                      {currentQB.level.error && (
                        <small className="invalid-feedback d-block">
                          {currentQB.level.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label>
                        Marks <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        placeholder="Enter marks"
                        maxLength={4}
                        value={currentQB.marks.value}
                        onChange={(e) => handleMarksChange(e.target.value)}
                        onBlur={(e) =>
                          setCurrentQB((p) => ({
                            ...p,
                            marks: {
                              ...p.marks,
                              value: e.target.value,
                              error: getQBValidationError(
                                "marks",
                                e.target.value,
                              ),
                            },
                          }))
                        }
                        className={currentQB.marks.error ? "is-invalid" : ""}
                      />
                      {currentQB.marks.error && (
                        <small className="invalid-feedback d-block">
                          {currentQB.marks.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>

              {(currentQB.type.value === "SINGLE_CHOICE" ||
                currentQB.type.value === "MULTIPLE_CHOICE") && (
                <FormGroup>
                  <Label>
                    Options <span className="text-danger">*</span>
                  </Label>

                  {currentQB.options.value.map((opt, index) => {
                    const filteredOptions = getFilteredGradeOptions(index);
                    const showOptionError =
                      currentQB.options.error &&
                      !hasContent(
                        hasEnglish && hasHindi
                          ? opt.textEnglish || opt.textHindi
                          : hasEnglish
                            ? opt.textEnglish
                            : opt.textHindi,
                      );

                    return (
                      <div key={index} className="mb-2">
                        <Row className="align-items-center">
                          <Col md="8">
                            <div
                              className={`quill-wrapper option-quill ${
                                showOptionError ? "quill-invalid" : ""
                              }`}
                            >
                              {assessmentLanguage === "English" && (
                                <>
                                  <Label className="text-muted small">
                                    {`Option ${index + 1}`}
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`Enter Option ${index + 1}`}
                                    value={opt.textEnglish || ""}
                                    onChange={(value) =>
                                      handleQuillChange(
                                        value,
                                        (validatedValue) =>
                                          updateOptionQuill(
                                            index,
                                            validatedValue,
                                            "textEnglish",
                                          ),
                                      )
                                    }
                                  />
                                </>
                              )}

                              {assessmentLanguage === "Hindi" && (
                                <>
                                  <Label className="text-muted small">
                                    Please enter option in Hindi
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`विकल्प ${index + 1} हिंदी में लिखें`}
                                    value={opt.textHindi || ""}
                                    onChange={(value) =>
                                      handleQuillChange(
                                        value,
                                        (validatedValue) =>
                                          updateOptionQuill(
                                            index,
                                            validatedValue,
                                            "textHindi",
                                          ),
                                      )
                                    }
                                  />
                                </>
                              )}

                              {assessmentLanguage === "English, Hindi" && (
                                <>
                                  <Label className="text-muted small">
                                    Option {index + 1} (English)
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`Enter option ${index + 1} in english`}
                                    value={opt.textEnglish || ""}
                                    onChange={(value) =>
                                      handleQuillChange(
                                        value,
                                        (validatedValue) =>
                                          updateOptionQuill(
                                            index,
                                            validatedValue,
                                            "textEnglish",
                                          ),
                                      )
                                    }
                                  />

                                  <Label className="text-muted small mt-3">
                                    Option {index + 1} (Hindi)
                                  </Label>
                                  <ReactQuill
                                    theme="snow"
                                    modules={quillModules}
                                    placeholder={`विकल्प ${index + 1} हिंदी में लिखें`}
                                    value={opt.textHindi || ""}
                                    onChange={(value) =>
                                      handleQuillChange(
                                        value,
                                        (validatedValue) =>
                                          updateOptionQuill(
                                            index,
                                            validatedValue,
                                            "textHindi",
                                          ),
                                      )
                                    }
                                  />
                                </>
                              )}
                            </div>
                            <div className="divider" />
                          </Col>
                          <Col md="3">
                            <Label className="text-muted small">Grade</Label>

                            <Select
                              options={filteredOptions}
                              value={
                                filteredOptions.find(
                                  (g) => g.value === opt.grade,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleQBOptionGradeChange(index, selected.value)
                              }
                              placeholder="Select grade"
                            />
                          </Col>

                          <Col
                            md="1"
                            className="d-flex align-items-center justify-content-center"
                          >
                            <Button
                              color="link"
                              className="p-0 table-action-delete"
                              disabled={currentQB.options.value.length <= 2}
                              onClick={() => removeQBOption(index)}
                            >
                              <i className="fa fa-trash text-danger cursor" />
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                  {currentQB.options.error && (
                    <small className="invalid-feedback d-block">
                      {currentQB.options.error}
                    </small>
                  )}

                  {currentQB.options.value.length < 5 && (
                    <Button color="link" onClick={addQBOption}>
                      <i className="fa-solid fa-plus" /> Option
                    </Button>
                  )}
                </FormGroup>
              )}

              {currentQB.type.value === "TRUE_FALSE" && (
                <FormGroup>
                  {currentQB.options.value.map((opt, index) => {
                    return (
                      <Row key={index} className="align-items-center mb-2">
                        <Col md="1">
                          <div className="text-muted no-wrap mt-4">
                            {opt.text}
                          </div>
                        </Col>

                        <Col md="4">
                          <Label className="text-muted small">
                            Grade <span className="text-danger">*</span>
                          </Label>

                          <Select
                            options={gradeOptions}
                            value={
                              gradeOptions.find((g) => g.value === opt.grade) ||
                              null
                            }
                            onChange={(selected) =>
                              handleQBOptionGradeChange(index, selected.value)
                            }
                            placeholder="Select grade"
                          />
                        </Col>
                      </Row>
                    );
                  })}

                  {currentQB.options.error && (
                    <small className="validation-error d-block mt-1">
                      {currentQB.options.error}
                    </small>
                  )}
                </FormGroup>
              )}

              <div className="text-right mt-3">
                <Button
                  color="deep-blue"
                  onClick={
                    qbPanelMode === "VIEW" ? saveQBQuestion : addQBQuestion
                  }
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-2" />
                    </>
                  ) : qbPanelMode === "VIEW" ? (
                    "Save Question"
                  ) : (
                    "Add Question"
                  )}
                </Button>
              </div>
            </CardBody>
          </Card>
        </RightPanelCard>
      )}
    </>
  );
};

export default AddQuestion;
